import React, { Suspense, useEffect, useRef, useState, useContext } from "react"

import { useCallback, useMemo } from "use-memo-one"

import { Canvas, useThree } from "react-three-fiber"

import { AppContext } from '../../context'
import { PageHighlightProvider } from '../../context/pageHighlightContext'

import "./custom-material"
import { Light } from "./light"
import { Loading, Start, useLoading, Waiting } from "./loading";

import { Controls } from "./controls"

import Effects from "./effects"
import Element, { PageHighlightElement } from "./element"
import { debounce } from "lodash"

import { useTextureLoader } from "./useTextureLoader"
import Hud from "../pages/hud"
import { OutlineProvider } from "../../context/outlineContext"
import { useIntl } from "gatsby-plugin-intl"
import * as THREE from "three"
import { Compass } from "../atoms/compass/index"

Object.assign(THREE.Scene.prototype, {
  dispose: function dispose() {
    console.log('app override:  THREE.Scene: .dispose() has been removed.');
  }
}); //

const Page = ({ ikey, index, step, size = 10, ratio = 1.3833, elements = [], videos = [], voiceOvers = [], sounds }) => {
  const elementImages = useTextureLoader(elements.map(({ image }) => image));
  const page = size * ratio;
  const offset = index * page - (step % 2 ? 0 : page / 2);
  const ref = useRef()
  return (
    <group ref={ref} position={[offset, 0, 0]} name={`page-${ikey}`}>
      {elements.map(({ ...props }, i) =>
        <Element key={`${ikey}-${i}`} ikey={`${ikey}-${i}`} map={elementImages[i]} {...props} pageOffset={offset} />
      )}
      <PageHighlightElement name={`page-${ikey}-highlight`} size={size} ratio={ratio} index={index} />
    </group>
  )
}

const Scene = () => {
  const { current: newCurrent, pages, pageStep, tableOfContents, setZoomDelta, started } = useContext(AppContext);
  const { scene } = useThree();
  const [transition, setTransition] = useState(true);
  const defaultTransitionDuration = 200;
  const [transitionDuration, setTransitionDuration] = useState(defaultTransitionDuration);
  const [current, setCurrent] = useState(0);
  const currentTimerRef = useRef();
  useEffect(() => {
    if (tableOfContents) {
      setTransitionDuration(0);
    }
    setTransition(true);
  }, [newCurrent]);

  useEffect(() => {
    if (transition && started) {
      clearTimeout(currentTimerRef.current);
      function tick() {
        setTransitionDuration(defaultTransitionDuration);
        setCurrent(newCurrent);
        setZoomDelta({ delta: -1.25 });
        setTransition(false);
      }
      currentTimerRef.current = setTimeout(tick, transitionDuration);
      return () => {
        clearTimeout(currentTimerRef.current);
      }
    }
  }, [transition, newCurrent]);


  const findOrphan = (item) => {
    if (item !== null && item.parent !== null) {
      return findOrphan(item.parent);
    }
    if (item.type === "Scene") {
      return null;
    }
    return item;
  }
  const checkOrphans = useCallback(debounce(() => {
    //  console.log("CHECK ORPHAN'S !!!!!", scene.__interaction);
    let requestNewCheck = false;
    scene.__interaction && scene.__interaction.forEach(function (item, index, object) {
      const orphan = findOrphan(item);
      if (orphan) {
        object.splice(index, 1);
        //  console.log("remove orphan", item, orphan);
        requestNewCheck = true;
      }
    });

    //console.log("requestNewCheck", requestNewCheck);
    if (requestNewCheck) {
      checkOrphans();
    }
  }, 300/*, { 'maxWait': 500 }*/), [scene]);

  useEffect(() => {
    checkOrphans();
  });

  const Pages = useMemo(() => {
    const spread = pages ? pages.slice(current, current + pageStep): [];
    return spread.map((props, index) => <Page step={pageStep} key={current + index} ikey={current + index} index={index} {...props} />)
  }, [current, pageStep, pages]);

  return (
    <scene>
      <OutlineProvider>
        <PageHighlightProvider>{Pages}</PageHighlightProvider>
        <Effects transition={transition} transitionDuration={transitionDuration} />
      </OutlineProvider>
    </scene>
  );
}

export default () => {
  const appContext = useContext(AppContext);
  const intl = useIntl();
  const loadingLabel = intl.formatMessage({ id: 'loading.label', defaultMessage: "loading" });

  const [loading, onStartLoading, onEndLoading] = useLoading();
  const { locale } = intl;

  return (
    <Start loading={loading}>
      <Compass dark/>
      <Canvas
        pixelRatio={typeof window !== `undefined` ? window.devicePixelRatio : 1}
        colorManagement={false}
        noEvents={loading}
        concurrent camera={{ position: [0, 0, appContext.zoom.current] }} style={{ position: 'fixed', zIndex: 0 }}>
        <AppContext.Provider value={{ locale, ...appContext }}>
          <Controls />
          <Light />
          <Suspense fallback={<Loading label={loadingLabel} onStartLoading={onStartLoading} onEndLoading={onEndLoading} />}>
            <Scene />
          </Suspense>
        </AppContext.Provider >
      </Canvas>
      <Hud />
      <Waiting />
    </Start>
  )
}